import React, { useMemo, useState } from 'react';
import { cn } from '../../utils/cn';

/**
 * LineChart — graphique SVG ultra-léger (zéro dépendance).
 *
 *   <LineChart
 *     data={[{ date: '2026-05-01', views: 12, uniques: 8 }, ...]}
 *     series={[
 *       { key: 'views',   label: 'Pages vues',         color: 'ink' },
 *       { key: 'uniques', label: 'Visiteurs uniques',  color: 'mute', dashed: true },
 *     ]}
 *     xKey="date"
 *     height={280}
 *   />
 *
 * Affiche les valeurs au hover. Filled area sous la première série.
 */
const COLORS = {
  ink:    'rgb(var(--ds-color-ink))',
  cream:  'rgb(var(--ds-color-cream))',
  mute:   'rgb(var(--ds-color-text-mute))',
  accent: 'rgb(var(--ds-color-accent, var(--ds-color-ink)))',
};

function getColor(name) {
  return COLORS[name] || name;
}

export function LineChart({
  data = [],
  series = [{ key: 'views', label: 'Vues', color: 'ink' }],
  xKey = 'date',
  height = 240,
  formatX = (v) => v,
  formatY = (v) => v,
  className,
}) {
  const [hover, setHover] = useState(null);

  const { width, padding, geometry } = useMemo(() => {
    const w = 800;
    const pad = { top: 12, right: 16, bottom: 28, left: 36 };
    const innerW = w - pad.left - pad.right;
    const innerH = height - pad.top - pad.bottom;

    const allValues = data.flatMap((d) => series.map((s) => d[s.key] || 0));
    const maxY = Math.max(1, ...allValues);
    const niceMaxY = niceCeil(maxY);

    const xStep = data.length > 1 ? innerW / (data.length - 1) : 0;
    const yScale = (v) => pad.top + innerH - (v / niceMaxY) * innerH;
    const xPos = (i) => pad.left + i * xStep;

    return {
      width: w,
      padding: pad,
      geometry: { innerW, innerH, maxY: niceMaxY, yScale, xPos, xStep },
    };
  }, [data, series, height]);

  if (!data.length) {
    return (
      <div className={cn('flex items-center justify-center h-[240px] text-sm text-mute', className)}>
        Pas de données pour cette période.
      </div>
    );
  }

  const { yScale, xPos, maxY } = geometry;

  // Lignes horizontales de fond (4 ticks)
  const ticks = Array.from({ length: 5 }).map((_, i) => {
    const v = Math.round((maxY * i) / 4);
    return { v, y: yScale(v) };
  });

  const buildPath = (sk) =>
    data
      .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xPos(i)} ${yScale(d[sk] || 0)}`)
      .join(' ');

  const buildArea = (sk) => {
    const start = `M ${xPos(0)} ${yScale(0)}`;
    const top = data.map((d, i) => `L ${xPos(i)} ${yScale(d[sk] || 0)}`).join(' ');
    const end = `L ${xPos(data.length - 1)} ${yScale(0)} Z`;
    return `${start} ${top} ${end}`;
  };

  // Indices à afficher sur l'axe X (4-6 labels max)
  const xTickIndices = (() => {
    const target = Math.min(6, data.length);
    if (data.length <= target) return data.map((_, i) => i);
    const step = (data.length - 1) / (target - 1);
    return Array.from({ length: target }, (_, k) => Math.round(k * step));
  })();

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * width;
    if (x < padding.left || x > width - padding.right) {
      setHover(null);
      return;
    }
    const idx = Math.round((x - padding.left) / geometry.xStep);
    setHover(Math.max(0, Math.min(data.length - 1, idx)));
  };

  return (
    <div className={cn('w-full', className)}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="w-full h-auto block"
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
      >
        {/* Grille horizontale */}
        {ticks.map((t, i) => (
          <g key={`grid-${i}`}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={t.y}
              y2={t.y}
              stroke="rgb(var(--ds-color-border) / 0.6)"
              strokeDasharray={i === 0 ? '0' : '2 4'}
              strokeWidth="1"
            />
            <text
              x={padding.left - 6}
              y={t.y + 3}
              textAnchor="end"
              fontSize="10"
              fontFamily="var(--ds-font-mono)"
              fill="rgb(var(--ds-color-text-mute))"
            >
              {formatY(t.v)}
            </text>
          </g>
        ))}

        {/* Ticks X */}
        {xTickIndices.map((i) => (
          <text
            key={`xt-${i}`}
            x={xPos(i)}
            y={height - 8}
            textAnchor={i === 0 ? 'start' : i === data.length - 1 ? 'end' : 'middle'}
            fontSize="10"
            fontFamily="var(--ds-font-mono)"
            fill="rgb(var(--ds-color-text-mute))"
          >
            {formatX(data[i]?.[xKey])}
          </text>
        ))}

        {/* Area pour la première série */}
        {series[0] && (
          <path
            d={buildArea(series[0].key)}
            fill={getColor(series[0].color)}
            opacity="0.07"
          />
        )}

        {/* Lignes */}
        {series.map((s) => (
          <path
            key={s.key}
            d={buildPath(s.key)}
            fill="none"
            stroke={getColor(s.color)}
            strokeWidth="1.8"
            strokeDasharray={s.dashed ? '4 4' : '0'}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {/* Hover line + points + tooltip */}
        {hover != null && data[hover] && (
          <g>
            <line
              x1={xPos(hover)}
              x2={xPos(hover)}
              y1={padding.top}
              y2={height - padding.bottom}
              stroke="rgb(var(--ds-color-ink))"
              strokeWidth="1"
              strokeDasharray="2 3"
              opacity="0.4"
            />
            {series.map((s) => (
              <circle
                key={`pt-${s.key}`}
                cx={xPos(hover)}
                cy={yScale(data[hover][s.key] || 0)}
                r="4"
                fill="rgb(var(--ds-color-surface))"
                stroke={getColor(s.color)}
                strokeWidth="2"
              />
            ))}
          </g>
        )}
      </svg>

      {/* Légende + valeur au hover */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-[12px]">
        {series.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5 text-mute">
            <span
              className="inline-block w-3 h-[2px]"
              style={{
                background: getColor(s.color),
                borderTop: s.dashed ? `2px dashed ${getColor(s.color)}` : 'none',
              }}
            />
            <span className="text-text">{s.label}</span>
            {hover != null && data[hover] && (
              <span className="font-mono text-mute ml-0.5">
                {formatY(data[hover][s.key] || 0)}
              </span>
            )}
          </span>
        ))}
        {hover != null && data[hover] && (
          <span className="ml-auto font-mono text-mute text-[11px]">
            {formatX(data[hover][xKey])}
          </span>
        )}
      </div>
    </div>
  );
}

function niceCeil(n) {
  if (n <= 1) return 1;
  const pow = 10 ** Math.floor(Math.log10(n));
  const norm = n / pow;
  if (norm <= 1) return pow;
  if (norm <= 2) return 2 * pow;
  if (norm <= 5) return 5 * pow;
  return 10 * pow;
}

export default LineChart;
